import { Gift } from "lucide-react";

const RoleButton = ({ active, testId, label, onClick }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={active ? "btn-primary text-sm py-2" : "btn-ghost text-sm py-2"}
    style={active ? {} : { border: "1px solid var(--border-stone)" }}
  >
    {label}
  </button>
);

// Role picker + full name + invite code (rendered before email/password).
export const SignupIdentityFields = ({ form, setForm, inviteRequired, onAffiliateClick }) => (
  <>
    <div>
      <label className="field-label">I am a…</label>
      <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Account type">
        <RoleButton
          active={form.role === "user"}
          testId="auth-role-client"
          label="Client / Family"
          onClick={() => setForm({ ...form, role: "user" })}
        />
        <RoleButton
          active={form.role === "attorney"}
          testId="auth-role-attorney"
          label="Attorney"
          onClick={() => setForm({ ...form, role: "attorney" })}
        />
        <button
          type="button"
          data-testid="auth-role-affiliate"
          onClick={onAffiliateClick}
          className="btn-ghost text-sm py-2"
          style={{ border: "1px solid var(--border-stone)" }}
          title="Affiliates apply for admin approval — you'll be redirected to the application form"
        >
          Affiliate
        </button>
      </div>
      {form.role === "user" && (
        <p className="text-[11px] mt-2" style={{ color: "var(--text-secondary)" }}>
          Filing a case for yourself or a family member? Pick <strong>Client / Family</strong>.
        </p>
      )}
      {form.role === "attorney" && (
        <p className="text-[11px] mt-2" style={{ color: "var(--text-secondary)" }}>
          Licensed immigration attorney reviewing client cases? Pick <strong>Attorney</strong>.
        </p>
      )}
      <p className="text-[11px] mt-2" style={{ color: "var(--text-secondary)" }} data-testid="auth-role-affiliate-note">
        Want to earn commission promoting DS-260USA.com? Click <strong>Affiliate</strong> —
        you'll be taken to the affiliate application (admin approval required).
      </p>
    </div>
    <div>
      <label className="field-label">Full name</label>
      <input
        data-testid="auth-name-input"
        className="field-input"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        required
        autoComplete="name"
      />
    </div>
    {inviteRequired && (
      <div>
        <label className="field-label">Invite code</label>
        <input
          data-testid="auth-invite-input"
          className="field-input"
          value={form.invite_code}
          onChange={(e) => setForm({ ...form, invite_code: e.target.value })}
          required
          autoComplete="off"
          placeholder="Enter the code your host shared with you"
        />
        <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
          DS-260USA.com is in private beta — access is by invitation only.
        </p>
      </div>
    )}
  </>
);

// Referral code + attorney firm fields (rendered after email/password).
export const SignupExtraFields = ({ form, setForm, initialRef }) => (
  <>
    <div>
      <label className="field-label flex items-center gap-1.5">
        <Gift className="w-3.5 h-3.5" style={{ color: "var(--action)" }} />
        Referral code (optional)
      </label>
      <input
        data-testid="auth-referral-input"
        type="text"
        className="field-input uppercase"
        value={form.referral_code}
        onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
        autoComplete="off"
        placeholder="e.g. MARIA23"
        maxLength={16}
      />
      {initialRef && (
        <p
          className="text-xs mt-1 flex items-center gap-1.5"
          data-testid="referral-prefilled-hint"
          style={{ color: "var(--success-text)" }}
        >
          <Gift className="w-3 h-3" /> A friend invited you! You'll each get $50 off when you file your first case.
        </p>
      )}
    </div>
    {form.role === "attorney" && (
      <>
        <div>
          <label className="field-label">Firm name</label>
          <input
            data-testid="auth-firm-input"
            className="field-input"
            value={form.firm_name}
            onChange={(e) => setForm({ ...form, firm_name: e.target.value })}
            placeholder="Wu Immigration Law"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label">Bar number</label>
            <input
              data-testid="auth-bar-number-input"
              className="field-input"
              value={form.bar_number}
              onChange={(e) => setForm({ ...form, bar_number: e.target.value })}
              placeholder="CA-123456"
            />
          </div>
          <div>
            <label className="field-label">State</label>
            <input
              data-testid="auth-bar-state-input"
              className="field-input"
              value={form.bar_state}
              onChange={(e) => setForm({ ...form, bar_state: e.target.value })}
              placeholder="CA"
              maxLength={4}
            />
          </div>
        </div>
      </>
    )}
  </>
);
